import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './addTasksNotesDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Child, ChildDaycare } from '@/types/Child'
import { CREATE_TASK_NOTE } from '@/modules/API/taskNote/taskNoteHandler.ts'
import { TaskNoteStatus, TaskNoteType } from '@/types/TaskNote.ts'
import moment, { Moment } from 'moment/moment'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import Loading from '@/components/Loading/Loading.tsx'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker.tsx'
import { Parent } from '@/types/Parent.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import {
    Assignment,
    AssignmentAdd,
    AssignmentTurnedIn,
    Call,
    CallMade,
    Cancel,
    CheckCircle,
    EditNote,
    Event,
    HighlightOff,
    ListAlt,
    People,
    PhoneDisabled,
    VideoCameraFront,
    Voicemail
} from '@mui/icons-material'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddTasksNotesDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    parentId?: number
    parentName?: string
    parents?: Parent[]
    childs?: Child[]
    childId?: number
    childName?: string
    staffs?: StaffWithLinks[]
    staffId?: number
    staffName?: string
    handleRefetch: () => void
    isRpeOrLaep: boolean
}

const AddTasksNotesDialog: FunctionComponent<AddTasksNotesDialogProps> = ({
    open = false,
    handleClose,
    daycareId,
    parentId,
    parentName,
    parents,
    childId,
    childName,
    childs,
    staffs,
    staffId,
    staffName,
    handleRefetch,
    isRpeOrLaep
}: AddTasksNotesDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const addTaskNoteForParent: boolean =
        parentName !== undefined && parentId !== undefined && (!parents || parents.length === 0)

    const addTaskNoteForChild: boolean =
        childName !== undefined && childId !== undefined && (!childs || childs.length === 0)

    const addTaskNoteForStaff: boolean =
        staffName !== undefined && staffId !== undefined && (!staffs || staffs.length === 0)

    const [taskNoteTitle, setTaskNoteTitle] = useState<string>('')
    const [taskNoteDescription, setTaskNoteDescription] = useState<string>('')
    const [taskNoteType, setTaskNoteType] = useState<string>(TaskNoteType.task)
    const [taskNoteInternalNote, setTaskNoteInternalNote] = useState<boolean>(false)

    const [taskStatus, setTaskStatus] = useState<string>(TaskNoteStatus.todo)
    const [taskDate, setTaskDate] = useState<string>(moment().format('DD-MM-YYYY'))

    const now: Moment = moment()
    const rounded: Moment = moment(Math.round(+now / (15 * 60 * 1000)) * (15 * 60 * 1000))

    const [taskStartHour, setTaskStartHour] = useState<string>(rounded.format('HH:mm'))
    const [taskEndHour, setTaskEndHour] = useState<string>(rounded.add(1, 'h').format('HH:mm'))

    const [taskNoteParentIds, setTaskNoteParentIds] = useState<string[]>(
        addTaskNoteForParent ? [parentId!.toString()] : []
    )
    const [taskNoteChildIds, setTaskNoteChildIds] = useState<string[]>(addTaskNoteForChild ? [childId!.toString()] : [])

    const [taskNoteStaffIds, setTaskNoteStaffIds] = useState<string[]>(addTaskNoteForStaff ? [staffId!.toString()] : [])

    const [createTaskNote, { loading: loadingCreate }] = useMutation(CREATE_TASK_NOTE, {
        variables: {
            daycareId: daycareId,
            taskNote: {
                title: taskNoteTitle,
                description: taskNoteDescription,
                type: taskNoteType,
                internalNote: taskNoteInternalNote,
                parentIds: taskNoteParentIds.map((id: string): number => Number(id)),
                childIds: taskNoteChildIds.map((id: string): number => Number(id)),
                staffIds: taskNoteStaffIds.map((id: string): number => Number(id)),
                status: taskNoteType !== TaskNoteType.note ? taskStatus : undefined,
                date: taskNoteType !== TaskNoteType.note ? taskDate : undefined,
                startHour: taskNoteType !== TaskNoteType.note ? taskStartHour : undefined,
                endHour: isAppointment(taskNoteType) ? taskEndHour : undefined
            }
        },
        onCompleted: () => {
            handleRefetch()
            handleClose()

            setTaskNoteTitle('')
            setTaskNoteDescription('')
            handleSelectTaskNoteType(TaskNoteType.note)
            setTaskNoteInternalNote(false)
            setTaskNoteChildIds(addTaskNoteForChild ? [childId!.toString()] : [])
            setTaskNoteParentIds(addTaskNoteForParent ? [parentId!.toString()] : [])
            setTaskNoteStaffIds(addTaskNoteForStaff ? [staffId!.toString()] : [])

            showNotificationSuccess(t('taskNoteAdded'))
        },
        onError: () => {
            showNotificationError(t('taskNoteAddedError'))
        }
    })

    const taskNoteTypeOptions: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: t('task'),
                    value: 'task',
                    startAdornment: <ListAlt className={styles.IconSelect} />
                },
                {
                    name: t('note'),
                    value: 'note',
                    startAdornment: <EditNote className={styles.IconSelect} />
                },
                {
                    name: t('call'),
                    value: 'call',
                    startAdornment: <Call className={styles.IconSelect} />
                },
                {
                    name: t('appointmentInPerson'),
                    value: 'appointmentInPerson',
                    startAdornment: <People className={styles.IconSelect} />
                },
                {
                    name: t('appointmentVideoconference'),
                    value: 'appointmentVideoconference',
                    startAdornment: <VideoCameraFront className={styles.IconSelect} />
                }
            ]
        }
    ]

    const taskNoteStatusOptions: SelectOptionsDeprecated = [
        {
            options: defineTaskNoteStatusOptions(taskNoteType, t)
        }
    ]

    const parentsOptions: SelectOptionsDeprecated = [
        {
            options: addTaskNoteForParent
                ? [
                      {
                          name: parentName!,
                          value: parentId!.toString()
                      }
                  ]
                : parents
                  ? parents
                        .sort((a: Parent, b: Parent): number => {
                            return a.lastname.localeCompare(b.lastname)
                        })
                        .map(
                            (parent: Parent): SelectOptionDeprecated => ({
                                name:
                                    (parent.firstname + ' ' + parent.lastname).trim() === ''
                                        ? parent.email
                                        : parent.firstname + ' ' + parent.lastname,
                                value: parent.id.toString()
                            })
                        )
                  : []
        }
    ]

    const childsOptions: SelectOptionsDeprecated = [
        {
            options: addTaskNoteForChild
                ? [
                      {
                          name: childName!,
                          value: childId!.toString()
                      }
                  ]
                : childs
                  ? childs
                        .filter((child: Child): boolean => {
                            return child.daycares.some(
                                (daycare: ChildDaycare): boolean => daycare.daycareId === daycareId && daycare.visible
                            )
                        })
                        .sort((a: Child, b: Child): number => {
                            return a.firstname.localeCompare(b.firstname)
                        })
                        .map(
                            (child: Child): SelectOptionDeprecated => ({
                                name: child.firstname + '' + getChildComplement(child, daycareId),
                                value: child.id.toString()
                            })
                        )
                  : []
        }
    ]

    const staffsOptions: SelectOptionsDeprecated = [
        {
            options: addTaskNoteForStaff
                ? [
                      {
                          name: staffName!,
                          value: staffId!.toString()
                      }
                  ]
                : staffs
                  ? staffs
                        .sort((a: StaffWithLinks, b: StaffWithLinks): number => {
                            return a.lastname.localeCompare(b.lastname)
                        })
                        .map(
                            (staff: StaffWithLinks): SelectOptionDeprecated => ({
                                name: staff.firstname + ' ' + staff.lastname,
                                value: staff.id.toString()
                            })
                        )
                  : []
        }
    ]

    function handleSelectTaskNoteType(value: string): void {
        setTaskNoteType(value)
        switch (value) {
            case TaskNoteType.appointmentInPerson:
            case TaskNoteType.appointmentVideoconference:
                setTaskStatus(TaskNoteStatus.schedule)
                break
            case TaskNoteType.task:
                setTaskStatus(TaskNoteStatus.todo)
                break
            case TaskNoteType.call:
                setTaskStatus(TaskNoteStatus.connected)
                break
        }
    }

    const disabled: boolean =
        taskNoteTitle === '' ||
        (taskNoteType !== TaskNoteType.note && (taskDate === '' || taskStartHour === '' || taskEndHour === '')) ||
        (taskNoteStaffIds.length === 0 && taskNoteChildIds.length === 0 && taskNoteParentIds.length === 0) ||
        loadingCreate

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t(defineDialogTitle(taskNoteType))} />
            {loadingCreate && <Loading overall={false} />}
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <SelectUnique
                            selectOptions={taskNoteTypeOptions}
                            selectValue={taskNoteType}
                            setSelectValue={handleSelectTaskNoteType}
                            formClassName={styles.UserInput}
                            fullWidth
                            label={t('type')}
                            required={true}
                        />
                    </div>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.Input}>
                        <UserInput
                            required={true}
                            label={t('title')}
                            type={'text'}
                            value={taskNoteTitle}
                            setValue={setTaskNoteTitle}
                            className={styles.UserInput}
                        />
                    </div>
                    <div className={styles.TextAreaInput}>
                        <p className={styles.TextAreaTitle}>{t('description')}</p>
                        <TextArea
                            placeholder={t('description')}
                            className={styles.TextArea}
                            value={taskNoteDescription}
                            onChange={setTaskNoteDescription}
                            maxCount={2000}
                        />
                    </div>
                    {taskNoteType !== TaskNoteType.note && (
                        <>
                            <div className={styles.Input}>
                                <SelectUnique
                                    label={t('status')}
                                    selectOptions={taskNoteStatusOptions}
                                    selectValue={taskStatus}
                                    setSelectValue={setTaskStatus}
                                    formClassName={styles.UserInput}
                                    fullWidth
                                />
                            </div>
                            <div className={styles.Input}>
                                <DatePickerComponent
                                    value={taskDate}
                                    setValue={setTaskDate}
                                    label={t(defineDateLabel(taskNoteType))}
                                    className={styles.UserInput}
                                    disablePast={false}
                                />
                                {!isAppointment(taskNoteType) && (
                                    <TimePickerComponent
                                        value={taskStartHour}
                                        setValue={setTaskStartHour}
                                        label={t(defineHourLabel(taskNoteType))}
                                        className={styles.UserInput}
                                        minuteToDisable={15}
                                    />
                                )}
                            </div>
                            {isAppointment(taskNoteType) && (
                                <div className={styles.Input}>
                                    <TimePickerComponent
                                        value={taskStartHour}
                                        setValue={setTaskStartHour}
                                        label={t(defineHourLabel(taskNoteType))}
                                        className={styles.UserInput}
                                        minuteToDisable={15}
                                    />
                                    <TimePickerComponent
                                        value={taskEndHour}
                                        setValue={setTaskEndHour}
                                        label={t('endHour')}
                                        className={styles.UserInput}
                                        minuteToDisable={15}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    <div className={styles.Input}>
                        <div className={styles.CheckBoxContainer}>
                            <Checkbox
                                onChange={setTaskNoteInternalNote}
                                checked={taskNoteInternalNote}
                                text={
                                    (parents || childs) && isRpeOrLaep
                                        ? t('visibleByPublic')
                                        : parents || childs
                                          ? t('visibleByParent')
                                          : t('visibleByStaff')
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.BreakLine}></div>
                    {(parents || childs) && (
                        <>
                            <div className={styles.Input}>
                                <SelectMultiple
                                    selectOptions={parentsOptions}
                                    selectValues={taskNoteParentIds}
                                    setSelectValues={setTaskNoteParentIds}
                                    className={styles.UserInput}
                                    label={isRpeOrLaep ? t('publics') : t('parents')}
                                    readOnly={addTaskNoteForParent && taskNoteParentIds.length === 1}
                                />
                            </div>
                            <div className={styles.Input}>
                                <SelectMultiple
                                    selectOptions={childsOptions}
                                    selectValues={taskNoteChildIds}
                                    setSelectValues={setTaskNoteChildIds}
                                    className={styles.UserInput}
                                    label={t('childs')}
                                    readOnly={addTaskNoteForChild && taskNoteChildIds.length === 1}
                                />
                            </div>
                        </>
                    )}
                    {(staffs || addTaskNoteForStaff) && (
                        <div className={styles.Input}>
                            <SelectMultiple
                                selectOptions={staffsOptions}
                                selectValues={taskNoteStaffIds}
                                setSelectValues={setTaskNoteStaffIds}
                                className={styles.UserInput}
                                label={t('staffs')}
                                readOnly={addTaskNoteForStaff && taskNoteStaffIds.length === 1}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'add'}
                        onClick={createTaskNote}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AddTasksNotesDialog

export function isAppointment(type: string): boolean {
    return type === TaskNoteType.appointmentInPerson || type === TaskNoteType.appointmentVideoconference
}

function defineDialogTitle(type: string): string {
    switch (type) {
        case TaskNoteType.note:
            return 'addNote'
        case TaskNoteType.task:
            return 'addTask'
        case TaskNoteType.call:
            return 'addCall'
        case TaskNoteType.appointmentInPerson:
        case TaskNoteType.appointmentVideoconference:
            return 'addAppointment'
        default:
            return 'addTask'
    }
}

export function defineHourLabel(type: string): string {
    switch (type) {
        case TaskNoteType.appointmentInPerson:
        case TaskNoteType.appointmentVideoconference:
            return 'startHour'
        case TaskNoteType.task:
            return 'dueHour'
        default:
            return 'hour'
    }
}

export function defineDateLabel(type: string): string {
    switch (type) {
        case TaskNoteType.task:
            return 'dueDate'
        default:
            return 'date'
    }
}

export function defineTaskNoteStatusOptions(type: string, t: (key: string) => string): SelectOptionDeprecated[] {
    switch (type) {
        case TaskNoteType.appointmentInPerson:
        case TaskNoteType.appointmentVideoconference:
            return [
                {
                    name: t('schedule'),
                    value: 'schedule',
                    startAdornment: <Event className={styles.IconSelect} />
                },
                {
                    name: t('done'),
                    value: 'done',
                    startAdornment: <CheckCircle className={styles.IconSelect} />
                },
                {
                    name: t('missed'),
                    value: 'missed',
                    startAdornment: <HighlightOff className={styles.IconSelect} />
                },
                {
                    name: t('cancelled'),
                    value: 'cancelled',
                    startAdornment: <Cancel className={styles.IconSelect} />
                }
            ]
        case TaskNoteType.call:
            return [
                {
                    name: t('connected'),
                    value: 'connected',
                    startAdornment: <CallMade className={styles.IconSelect} />
                },
                {
                    name: t('noAnswer'),
                    value: 'noAnswer',
                    startAdornment: <PhoneDisabled className={styles.IconSelect} />
                },
                {
                    name: t('leftMessage'),
                    value: 'leftMessage',
                    startAdornment: <Voicemail className={styles.IconSelect} />
                }
            ]
        default:
            return [
                {
                    name: t('todo'),
                    value: 'todo',
                    startAdornment: <AssignmentAdd className={styles.IconSelect} />
                },
                {
                    name: t('inProgress'),
                    value: 'inProgress',
                    startAdornment: <Assignment className={styles.IconSelect} />
                },
                {
                    name: t('done'),
                    value: 'done',
                    startAdornment: <AssignmentTurnedIn className={styles.IconSelect} />
                }
            ]
    }
}

export function getChildComplement(child: Child, daycareId: number): string {
    const complement: string | undefined = child.daycares.find(
        (daycare: ChildDaycare): boolean => daycare.daycareId === daycareId
    )?.complement
    return complement ? ' - ' + complement : ''
}
